import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  afterRenderEffect,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { SkeletonLoaderLegacyComponent } from '../../skeleton-loader-legacy/skeleton-loader-legacy.component';
import { AssetComponent } from '@mkp/shared/ui-asset';
import { DataTestDirective } from '@mkp/tracking/feature-tracking';
import { TranslateModule } from '@ngx-translate/core';
import {
  BadgeComponent,
  BadgeConfig,
  BadgeType,
  LabelComponent,
  LabelConfig,
} from '../../../elements';
import { TabInkBarDirective } from '../tab-ink-bar.directive';
import { TabItemComponent } from '../tab-item/tab-item.component';
import { TabSize } from '../tab.config';

@Component({
  selector: 'mds-tab-header',
  templateUrl: './tab-header.component.html',
  styleUrl: './tab-header.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    NgTemplateOutlet,
    TabInkBarDirective,
    DataTestDirective,
    MatIconModule,
    BadgeComponent,
    LabelComponent,
    AssetComponent,
    TranslateModule,
    SkeletonLoaderLegacyComponent,
  ],
})
export class TabHeaderComponent<TabId, Template = undefined> implements AfterViewInit {
  readonly tabs = input.required<QueryList<TabItemComponent<TabId, Template>>>();
  readonly disabled = input.required<boolean>();
  readonly size = input.required<TabSize>();
  readonly handleClick = output<TabId>();
  @ViewChild(TabInkBarDirective, { static: true }) inkBar!: TabInkBarDirective;
  @ViewChild('tabHeaderWrapper') tabHeaderWrapper!: ElementRef;
  @ViewChildren('tabItem') tabItems!: QueryList<ElementRef>;
  @ViewChildren(BadgeComponent) badges!: QueryList<ElementRef>;

  readonly classes = computed<string>(() => getSizeClasses(this.size()));
  readonly badgeConfig = computed<BadgeConfig>(() => ({ type: getBadgeType(this.size()) }));
  readonly labelConfig: LabelConfig = { color: 'blue' };
  private readonly renderer = inject(Renderer2);
  private readonly destroy = inject(DestroyRef);

  _ = afterRenderEffect(() => {
    const activeTabIndex =
      this.tabs()
        .toArray()
        .findIndex((tab) => tab.active()) ?? 0;
    this.alignInkBarToSelectedTab(activeTabIndex);
    this.alignInkBarWhenBadgesChange(activeTabIndex);
  });

  ngAfterViewInit(): void {
    this.onScroll();
  }

  selectTab(tab: TabItemComponent<TabId, Template>, index: number) {
    tab.active.set(true);
    this.alignInkBarToSelectedTab(index);

    this.handleClick.emit(tab.id());
  }

  onScroll() {
    if (!this.tabHeaderWrapper?.nativeElement) {
      return;
    }

    const isEnd =
      this.tabHeaderWrapper.nativeElement.offsetWidth +
        this.tabHeaderWrapper.nativeElement.scrollLeft ===
      this.tabHeaderWrapper.nativeElement.scrollWidth;

    isEnd
      ? this.renderer.removeClass(this.tabHeaderWrapper.nativeElement, 'overflow-opacity')
      : this.renderer.addClass(this.tabHeaderWrapper.nativeElement, 'overflow-opacity');
  }

  private alignInkBarToSelectedTab(index: number): void {
    if (!this.inkBar) {
      return;
    }

    const activeTabRef = this.tabItems && index > -1 ? this.tabItems.toArray()[index] : null;

    if (activeTabRef?.nativeElement) {
      this.inkBar.alignToElement(activeTabRef.nativeElement);
    }
  }

  private alignInkBarWhenBadgesChange(index: number) {
    this.badges.changes
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => this.alignInkBarToSelectedTab(index));
  }
}

const getBadgeType = (tabSize: TabSize): BadgeType =>
  tabSize === TabSize.Large ? BadgeType.Large : BadgeType.Small;
const getSizeClasses = (tabSize: TabSize): string =>
  tabSize === TabSize.Small ? ` tab-small body-s` : ` tab-large body-default`;
